import { useState, useEffect } from "react";
import { MbCard } from "@/components/MbCard";
import { MbBadgeSun } from "@/components/MbBadgeSun";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { CheckCircle2, ArrowLeft, ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { AppFooter } from "@/components/AppFooter";
import { isRestrictedContext, openInNewTab } from "@/lib/browser-utils";
import { Alert, AlertDescription } from "@/components/ui/alert";

declare global {
  interface Window {
    Razorpay: any;
  }
}

export default function Subscribe() {
  const [processing, setProcessing] = useState(false);
  const [razorpayLoaded, setRazorpayLoaded] = useState(false);
  const [inRestrictedContext, setInRestrictedContext] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { logo, strings } = useLanguage();

  // Check authentication status on mount
  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast({
          title: "Authentication Required",
          description: "Please login to subscribe to our premium service.",
          variant: "destructive"
        });
        navigate('/auth');
      }
    };
    checkAuth();
  }, [navigate, toast]);

  // Check if in restricted context (iframe with storage restrictions)
  useEffect(() => {
    setInRestrictedContext(isRestrictedContext());
  }, []);

  // Load Razorpay script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    
    script.onload = () => {
      console.log('Razorpay script loaded successfully');
      setRazorpayLoaded(true);
    };
    
    script.onerror = () => {
      console.error('Failed to load Razorpay script');
      toast({
        title: "Error",
        description: "Payment gateway failed to load. Please check your internet connection and refresh.",
        variant: "destructive"
      });
    };
    
    document.body.appendChild(script);
    
    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, [toast]);

  const handleSubscribe = async () => {
    setProcessing(true);

    try {
      // Get current user with SecurityError handling
      let user;
      try {
        const { data: { user: authUser }, error: userError } = await supabase.auth.getUser();
        if (userError) throw userError;
        user = authUser;
      } catch (authError: any) {
        // Handle SecurityError specifically (occurs in restricted iframe contexts)
        if (authError?.name === 'SecurityError' || authError?.message?.includes('LockManager')) {
          toast({
            title: "Security Restriction",
            description: "Please open this page in a new tab to complete your subscription.",
            variant: "destructive"
          });
          setProcessing(false);
          return;
        }
        throw authError;
      }

      if (!user) {
        toast({
          title: "Error",
          description: "Please login to subscribe",
          variant: "destructive"
        });
        navigate('/auth');
        setProcessing(false);
        return;
      }

      // Get user profile with email
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('id, name, phone, email')
        .eq('auth_user_id', user.id)
        .single();

      if (profileError || !profile) {
        console.error('Profile fetch error:', profileError);
        toast({
          title: "Error",
          description: "Profile not found. Please try again.",
          variant: "destructive"
        });
        setProcessing(false);
        return;
      }

      console.log('Profile loaded:', { id: profile.id, hasEmail: !!profile.email });

      // Track checkout started
      await supabase.from('payment_events').insert({
        user_id: profile.id,
        event_type: 'checkout_started',
        amount: 999,
        currency: 'INR',
      });

      // Check if Razorpay script is loaded
      if (!window.Razorpay || !razorpayLoaded) {
        console.error('Razorpay not available. Window.Razorpay:', !!window.Razorpay, 'Loaded state:', razorpayLoaded);
        toast({
          title: "Error",
          description: "Payment gateway is still loading. Please wait a moment and try again.",
          variant: "destructive"
        });
        setProcessing(false);
        return;
      }

      console.log('Creating Razorpay order...');

      // Create order
      const { data: orderData, error: orderError } = await supabase.functions
        .invoke('create-razorpay-order', {
          body: {
            amount: 999,
            currency: 'INR',
            userId: profile.id,
            planCode: 'SMART_FARMER_3Y999'
          }
        });

      if (orderError || !orderData) {
        console.error('Order creation error:', orderError, orderData);
        
        // Track order creation failure
        await supabase.from('payment_events').insert({
          user_id: profile.id,
          event_type: 'order_creation_failed',
          error_message: orderError?.message || 'Failed to create order',
          amount: 999,
        });
        
        toast({
          title: "Error",
          description: orderError?.message || "Failed to create order. Please try again.",
          variant: "destructive"
        });
        setProcessing(false);
        return;
      }

      console.log('Order created successfully:', orderData.orderId);

      // Track payment initiated
      await supabase.from('payment_events').insert({
        user_id: profile.id,
        event_type: 'payment_initiated',
        order_id: orderData.orderId,
        amount: orderData.amount,
        currency: orderData.currency,
      });

      // Open Razorpay checkout
      console.log('Opening Razorpay checkout...');
      
      // Prepare user data for prefill with fallbacks
      const userEmail = profile.email || user.email || `${profile.phone}@temp.example.com`;
      const userName = profile.name || user.user_metadata?.name || 'User';
      const userPhone = profile.phone || user.phone || '';
      
      console.log('Prefill data:', { email: userEmail, name: userName, phone: userPhone });
      
      const options = {
        key: orderData.keyId,
        amount: orderData.amount,
        currency: orderData.currency,
        order_id: orderData.orderId,
        name: "Smart Farmer Premium",
        description: strings.oneYearFullAccess,
        image: logo,
        handler: async (response: any) => {
          console.log('Payment completed, verifying...');
          try {
            // Verify payment
            const { data: verifyData, error: verifyError } = await supabase.functions
              .invoke('verify-razorpay-payment', {
                body: {
                  orderId: response.razorpay_order_id,
                  paymentId: response.razorpay_payment_id,
                  signature: response.razorpay_signature,
                  userId: profile.id,
                  planCode: 'SMART_FARMER_3Y999'
                }
              });

            if (verifyError || !verifyData?.success) {
              console.error('Payment verification error:', verifyError);
              
              // Track payment failed
              await supabase.from('payment_events').insert({
                user_id: profile.id,
                event_type: 'payment_failed',
                order_id: response.razorpay_order_id,
                error_message: verifyError?.message || 'Payment verification failed',
                amount: 999,
              });
              
              toast({
                title: "Payment Failed",
                description: "Payment verification failed. Please contact support.",
                variant: "destructive"
              });
              setProcessing(false);
              return;
            }

            // Track payment success
            await supabase.from('payment_events').insert({
              user_id: profile.id,
              event_type: 'payment_success',
              order_id: response.razorpay_order_id,
              payment_id: response.razorpay_payment_id,
              amount: 999,
            });

            toast({
              title: strings.paySuccess,
              description: strings.subscriptionActivated,
            });
            
            setProcessing(false);
            navigate("/today");
          } catch (error) {
            console.error('Payment handler error:', error);
            toast({
              title: "Error",
              description: "Something went wrong. Please try again.",
              variant: "destructive"
            });
            setProcessing(false);
          }
        },
        prefill: {
          name: userName,
          email: userEmail,
          contact: userPhone
        },
        theme: {
          color: "#10b981"
        },
        modal: {
          ondismiss: () => {
            setProcessing(false);
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      
      razorpay.on('payment.failed', function (response: any) {
        console.error('Razorpay payment failed:', response.error);
        toast({
          title: "Payment Failed",
          description: response.error.description || "Payment failed. Please try again.",
          variant: "destructive"
        });
        setProcessing(false);
      });
      
      razorpay.open();
      console.log('Razorpay modal opened');

    } catch (error: any) {
      console.error('Subscribe error:', error);
      
      // More specific error messages
      let errorMessage = "Something went wrong. Please try again.";
      if (error?.message) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      }
      
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive"
      });
      setProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-[hsl(var(--mb-sky))]">
      {/* Header */}
      <header className="container mx-auto px-4 py-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={logo} alt="દમયંતી બાગ" className="h-12" />
          </div>
          <Button variant="ghost" onClick={() => navigate("/")}>
            <ArrowLeft className="mr-2 w-4 h-4" />
            {strings.back}
          </Button>
        </div>
      </header>

      {/* Subscription Card */}
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          {/* Restricted Context Warning */}
          {inRestrictedContext && (
            <Alert className="mb-6 border-amber-500 bg-amber-50 dark:bg-amber-950/20">
              <ExternalLink className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-800 dark:text-amber-200">
                <div className="flex items-center justify-between gap-4">
                  <span>For security, payments must be completed in a new tab.</span>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => openInNewTab('/subscribe')}
                    className="flex-shrink-0"
                  >
                    <ExternalLink className="mr-2 h-4 w-4" />
                    Open in New Tab
                  </Button>
                </div>
              </AlertDescription>
            </Alert>
          )}
          
          <MbCard elevated className="overflow-hidden">
            <div className="bg-gradient-to-r from-[hsl(var(--mb-green-dark))] to-[hsl(var(--mb-green-light))] p-8 text-white text-center">
              <MbBadgeSun className="mb-4">{strings.premium}</MbBadgeSun>
              <h1 className="text-3xl font-bold mb-2">{strings.pricing.title}</h1>
              <div className="text-5xl font-bold mb-2">{strings.pricing.price}</div>
              <p className="text-white/90">{strings.oneYearFullAccess}</p>
              <p className="text-xs text-white/60 mt-1">{strings.limitedOffer}</p>
            </div>

            <div className="p-8 space-y-6">
              <div>
                <h2 className="text-xl font-bold mb-4">{strings.whatYouGet}</h2>
                <ul className="space-y-4">
                  {strings.pricing.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-lg">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <Button
                variant="hero"
                size="xl"
                className="w-full"
                onClick={handleSubscribe}
                disabled={processing || !razorpayLoaded || inRestrictedContext}
              >
                {inRestrictedContext 
                  ? "Open in New Tab to Subscribe" 
                  : !razorpayLoaded 
                  ? "Loading payment gateway..." 
                  : processing 
                  ? strings.processing 
                  : strings.subscribeNow}
              </Button>

              <div className="text-center text-sm text-muted-foreground">
                <p>{strings.securePayment}</p>
              </div>
            </div>
          </MbCard>

          {/* Benefits Section */}
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
            <MbCard className="p-6 text-center">
              <div className="text-4xl mb-2">🌾</div>
              <h3 className="font-bold mb-1">{strings.dailyNew}</h3>
              <p className="text-sm text-muted-foreground">
                {strings.dailyNewDesc}
              </p>
            </MbCard>

            <MbCard className="p-6 text-center">
              <div className="text-4xl mb-2">💬</div>
              <h3 className="font-bold mb-1">{strings.directContact}</h3>
              <p className="text-sm text-muted-foreground">
                {strings.directContactDesc}
              </p>
            </MbCard>

            <MbCard className="p-6 text-center">
              <div className="text-4xl mb-2">👥</div>
              <h3 className="font-bold mb-1">{strings.community}</h3>
              <p className="text-sm text-muted-foreground">
                {strings.communityDesc}
              </p>
            </MbCard>
          </div>
        </div>
      </div>
      
      <AppFooter />
    </div>
  );
}
